import {
  useCallback,
  useEffect,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';

import { BrowseSidebarUserFooter } from './BrowseSidebarUserFooter';
import { SidebarCollapsedContext } from './sidebarCollapsed';

const STORAGE_KEY = 'ptv2:sidebar:collapsed';

function readPersisted(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writePersisted(next: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
  } catch {
    // Ignore storage errors (private mode, quota).
  }
}

function isSidebarItemActive(
  pathname: string,
  hash: string,
  to: string,
  matchEnd: boolean,
  defaultActive: boolean,
): boolean {
  // Hash-bearing entries (e.g. /patients/X#demographics) are anchors into a
  // single page; they're "active" only when the path matches AND the hash
  // matches the entry's target. Without this branch the path-only check
  // would mark ALL hash entries on the same path as active simultaneously.
  // `defaultActive` lets a single hash entry claim the highlight when the
  // URL has no hash yet (e.g. fresh navigation to /patients/X).
  const hashIndex = to.indexOf('#');
  if (hashIndex !== -1) {
    const path = to.slice(0, hashIndex);
    const targetHash = to.slice(hashIndex);
    if (pathname !== path) return false;
    if (hash === targetHash) return true;
    return hash === '' && defaultActive;
  }
  if (matchEnd) return pathname === to;
  return pathname === to || pathname.startsWith(`${to}/`);
}

export interface SidebarItem {
  readonly key: string;
  readonly label: string;
  readonly to: string;
  readonly matchEnd?: boolean;
  readonly icon?: ReactElement | null;
  readonly count?: number | null;
  /**
   * For hash-anchor entries: claim the active highlight when the URL has no
   * hash. Use sparingly — at most one item per group should set this.
   */
  readonly defaultActive?: boolean;
  /**
   * Render the row as non-interactive (greyed out, no navigation, removed
   * from the tab order). Useful for "coming soon" placeholders so the
   * affordance still discloses the eventual destination.
   */
  readonly disabled?: boolean;
}

export interface SidebarGroup {
  readonly key: string;
  readonly heading?: string;
  readonly items: readonly SidebarItem[];
}

interface SidebarProps {
  readonly groups: readonly SidebarGroup[];
  /**
   * Override the default rail head (logo + "Jump to…"). Pass `null` to
   * suppress; omit to use the default.
   */
  readonly header?: ReactNode | null;
  /**
   * Override the default rail user footer. Pass `null` to suppress;
   * omit to use the default (`BrowseSidebarUserFooter`).
   */
  readonly footer?: ReactNode | null;
}

export function Sidebar({ groups, header, footer }: SidebarProps): ReactElement {
  const { t } = useTranslation();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState<boolean>(readPersisted);

  useEffect(() => {
    writePersisted(collapsed);
  }, [collapsed]);

  const toggle = useCallback(() => {
    setCollapsed((prev) => !prev);
  }, []);

  const resolvedHeader = header === undefined ? null : header;
  const resolvedFooter =
    footer === undefined ? <BrowseSidebarUserFooter /> : footer;

  return (
    <SidebarCollapsedContext.Provider value={collapsed}>
      <aside
        aria-label={t('shell.sidebar.ariaLabel')}
        data-collapsed={collapsed ? 'true' : 'false'}
        data-surface="sidebar"
        className={cn(
          'bg-card border-r border-border flex flex-col h-full flex-shrink-0 transition-[width] duration-150 ease-out',
          collapsed ? 'w-[60px]' : 'w-[232px]',
        )}
      >
        {resolvedHeader}
        <nav
          aria-label={t('shell.sidebar.navAriaLabel')}
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
        >
          {groups.length === 0 ? (
            !collapsed && (
              <p className="p-4 text-sm text-muted-foreground">
                {t('shell.sidebar.empty')}
              </p>
            )
          ) : (
            groups.map((group, groupIdx) => (
              <div key={group.key} className="px-2 pt-2 pb-1">
                {groupIdx > 0 && <Separator className="mb-2" aria-hidden="true" />}
                {!collapsed && group.heading !== undefined && (
                  <p className="px-2.5 pt-2 pb-1.5 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/70 select-none">
                    {group.heading}
                  </p>
                )}
                <div className="space-y-px">
                  {group.items.map((item) => {
                    const active = isSidebarItemActive(
                      location.pathname,
                      location.hash,
                      item.to,
                      item.matchEnd === true,
                      item.defaultActive === true,
                    );
                    return (
                      <SidebarLink
                        key={item.key}
                        item={item}
                        active={active}
                        collapsed={collapsed}
                      />
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </nav>
        <CollapseToggle collapsed={collapsed} onToggle={toggle} />
        {resolvedFooter !== null && resolvedFooter !== false && (
          <div
            className={cn(
              'border-t border-border',
              collapsed ? 'p-2 flex justify-center' : 'p-3',
            )}
          >
            {resolvedFooter}
          </div>
        )}
      </aside>
    </SidebarCollapsedContext.Provider>
  );
}

interface SidebarLinkProps {
  readonly item: SidebarItem;
  readonly active: boolean;
  readonly collapsed: boolean;
}

function SidebarLink({ item, active, collapsed }: SidebarLinkProps): ReactElement {
  const baseClasses = cn(
    'flex items-center rounded-md text-[13.5px] font-medium transition-colors',
    collapsed
      ? 'justify-center w-10 h-9 mx-auto'
      : 'justify-between gap-2 w-full text-left px-2.5 py-1.5',
  );

  const inner = (
    <>
      <span
        className={cn(
          'flex items-center min-w-0',
          collapsed ? 'gap-0' : 'gap-2.5 truncate',
        )}
      >
        {item.icon !== null && item.icon !== undefined && (
          <span
            className={cn(
              'shrink-0',
              active && !item.disabled ? 'text-brand-soft-foreground' : 'text-muted-foreground',
            )}
          >
            {item.icon}
          </span>
        )}
        {!collapsed && <span className="truncate">{item.label}</span>}
      </span>
      {!collapsed && item.count !== null && item.count !== undefined && (
        <span
          className={cn(
            'ml-auto shrink-0 rounded-full px-2 py-px text-[10.5px] font-medium tabular-nums',
            active && !item.disabled
              ? 'bg-brand-soft-foreground/10 text-brand-soft-foreground'
              : 'bg-muted text-muted-foreground',
          )}
        >
          {item.count}
        </span>
      )}
    </>
  );

  // Disabled rows render as a non-interactive span: not focusable, no
  // navigation, and visually greyed. Same DOM shape as the active row so the
  // sidebar's vertical rhythm doesn't shift when an item flips.
  if (item.disabled === true) {
    return (
      <span
        aria-disabled="true"
        title={collapsed ? item.label : undefined}
        className={cn(baseClasses, 'cursor-not-allowed text-muted-foreground/60 select-none')}
      >
        {inner}
      </span>
    );
  }

  return (
    <Link
      to={item.to}
      aria-current={active ? 'page' : undefined}
      title={collapsed ? item.label : undefined}
      className={cn(
        baseClasses,
        active
          ? 'bg-brand-soft text-brand-soft-foreground'
          : 'text-foreground/80 hover:bg-accent hover:text-foreground',
      )}
    >
      {inner}
    </Link>
  );
}

interface CollapseToggleProps {
  readonly collapsed: boolean;
  readonly onToggle: () => void;
}

function CollapseToggle({ collapsed, onToggle }: CollapseToggleProps): ReactElement {
  const { t } = useTranslation();
  const label = collapsed
    ? t('shell.sidebar.expand')
    : t('shell.sidebar.collapse');
  const Icon = collapsed ? PanelLeftOpen : PanelLeftClose;
  return (
    <div className={cn('px-2 py-1.5', collapsed && 'flex justify-center')}>
      <button
        type="button"
        onClick={onToggle}
        aria-label={label}
        aria-expanded={!collapsed}
        title={collapsed ? label : undefined}
        className={cn(
          'flex items-center text-[12px] font-medium text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors',
          collapsed
            ? 'justify-center w-10 h-9'
            : 'justify-start gap-2 w-full px-2.5 py-1.5',
        )}
      >
        <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
        {!collapsed && <span>{label}</span>}
      </button>
    </div>
  );
}
